import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { GanttDocument } from "@/types/gantt";

// Per-window gantt bodies, keyed by ganttDocId (TODO §26/§3.3). A mirror of `diagramSlice`,
// and for the same reason: dragging one bar across a six-month timeline emits a whole
// schedule on a ~400ms debounce, and folding that into `DeckDocument` would rewrite the
// entire deck record per drag.
export type GanttState = Record<string, GanttDocument>;

const initialState: GanttState = {};

const ganttSlice = createSlice({
  name: "gantt",
  initialState,
  reducers: {
    // §17.4.5 MERGES a map in, rather than replacing it — the nugget path's one new
    // reducer. Inserting a nugget brings its gantt schedules into a session that is already
    // holding this deck's, so `hydrated` (which replaces) would wipe the deck.
    //
    // It is deliberately NOT in the persistence listener's matcher: these bodies were just
    // READ out of IndexedDB under the nugget's owner key, and writing them straight back —
    // under this deck's owner, which is what a naive listener would do — is the exact fork
    // §17.1.5 exists to prevent.
    ganttDocsMerged(state, action: PayloadAction<GanttState>) {
      Object.assign(state, action.payload);
    },
    // Replaces the whole map on IndexedDB hydration.
    hydrated(_state, action: PayloadAction<GanttState>) {
      return action.payload;
    },
    ganttDocCreated(
      state,
      action: PayloadAction<{ docId: string; doc: GanttDocument }>,
    ) {
      state[action.payload.docId] = action.payload.doc;
    },
    ganttDocUpdated(
      state,
      action: PayloadAction<{ docId: string; doc: GanttDocument }>,
    ) {
      state[action.payload.docId] = action.payload.doc;
    },
    // Unlinks the body from this session. Whether the IndexedDB row is actually DELETED is
    // answered in the persistence listener against the row's owner key (§3.3.3 / §17.6.2),
    // exactly as it is for diagrams.
    ganttDocRemoved(state, action: PayloadAction<{ docId: string }>) {
      delete state[action.payload.docId];
    },
  },
});

export const {
  hydrated,
  ganttDocsMerged,
  ganttDocCreated,
  ganttDocUpdated,
  ganttDocRemoved,
} = ganttSlice.actions;

export default ganttSlice.reducer;
